// AI Artifact Component — OFFSET
// Rows rectangulaires hairline : icône carrée + chemin mono + flèche.

import React, { memo, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, Copy, Check, FileCode, Terminal, Database, Box, ArrowUpRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PxMark } from '@/components/PxMark';

export interface AIArtifactProps {
    title: string;
    description?: string;
    type?: 'code' | 'file' | 'shell' | 'supabase' | 'custom';
    icon?: React.ReactNode;
    actions?: React.ReactNode;
    defaultCollapsed?: boolean;
    className?: string;
    children: React.ReactNode;
}

export const AIArtifact = memo(({
    title,
    description,
    type = 'code',
    icon,
    actions,
    defaultCollapsed = false,
    className,
    children
}: AIArtifactProps) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

    const typeIcons = {
        code: <FileCode className="h-3.5 w-3.5" />,
        file: <FileCode className="h-3.5 w-3.5" />,
        shell: <Terminal className="h-3.5 w-3.5" />,
        supabase: <Database className="h-3.5 w-3.5" />,
        custom: <Box className="h-3.5 w-3.5" />
    };

    return (
        <div
            className={cn(
                "border border-border bg-card overflow-hidden",
                className
            )}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                <div className="h-6 w-6 bg-secondary text-foreground flex items-center justify-center shrink-0">
                    {icon || typeIcons[type]}
                </div>

                <div className="flex-1 min-w-0">
                    <h4 className="text-[13px] font-medium text-foreground truncate">{title}</h4>
                    {description && (
                        <p className="font-mono text-[11px] text-faint truncate">{description}</p>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {actions}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="p-1 text-faint hover:bg-secondary hover:text-foreground transition-colors"
                    >
                        {isCollapsed ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronUp className="h-4 w-4" />
                        )}
                    </button>
                </div>
            </div>

            {/* Content */}
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        className="overflow-hidden"
                    >
                        <div className="p-3">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
});

AIArtifact.displayName = 'AIArtifact';

// Artifact Action Item (for listing files/actions)
export interface AIArtifactItemProps {
    icon?: React.ReactNode;
    label: string;
    sublabel?: string;
    status?: 'pending' | 'loading' | 'complete' | 'error';
    onClick?: () => void;
    className?: string;
}

export const AIArtifactItem = memo(({
    icon,
    label,
    sublabel,
    status = 'complete',
    onClick,
    className
}: AIArtifactItemProps) => {
    return (
        <div
            onClick={onClick}
            className={cn(
                "flex items-center gap-2 border border-border bg-card px-2.5 py-1.5",
                onClick && "cursor-pointer hover:bg-secondary transition-colors",
                className
            )}
        >
            <div className="h-5 w-5 bg-secondary flex items-center justify-center shrink-0">
                {status === 'loading' ? (
                    <PxMark size={10} state="generating" />
                ) : status === 'complete' ? (
                    icon || <Check className="h-3 w-3 text-ok" />
                ) : status === 'error' ? (
                    <X className="h-3 w-3 text-err" />
                ) : (
                    icon || <span className="h-1.5 w-1.5 bg-faint/40" />
                )}
            </div>

            <div className="flex-1 min-w-0">
                <span className={cn(
                    "block truncate font-mono text-[11px]",
                    status === 'loading' && "text-foreground",
                    status === 'complete' && "text-muted-foreground",
                    status === 'error' && "text-err",
                    status === 'pending' && "text-faint"
                )}>
                    {label}
                </span>
                {sublabel && (
                    <span className="block truncate font-mono text-[11px] text-faint">{sublabel}</span>
                )}
            </div>

            {onClick && (
                <ArrowUpRight className="h-3 w-3 shrink-0 text-faint" />
            )}
        </div>
    );
});

AIArtifactItem.displayName = 'AIArtifactItem';

// Copy Button for code blocks
export const AIArtifactCopyButton = memo(({ content }: { content: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="p-1 text-faint hover:bg-secondary hover:text-foreground transition-colors"
            title="Copier"
        >
            {copied ? (
                <Check className="h-3.5 w-3.5 text-ok" />
            ) : (
                <Copy className="h-3.5 w-3.5" />
            )}
        </button>
    );
});

AIArtifactCopyButton.displayName = 'AIArtifactCopyButton';

export default AIArtifact;
